/**
 * Safe storage utilities for the Client Workspace.
 *
 * Wraps localStorage/sessionStorage access in try/catch to handle:
 * - QuotaExceededError (storage full)
 * - SecurityError (third-party cookie blocking, iframe restrictions)
 * - Storage being null/undefined (SSR or restricted environments)
 *
 * Used by:
 * - SortControls (sessionStorage) — persists sort preference per session
 * - ViewModeToggle (localStorage) — persists view mode across sessions
 *
 * @module storage
 * @see Requirements 4.3, 7.4
 */

/**
 * Safely retrieves a value from storage.
 * Returns null if storage is unavailable or throws.
 */
export function safeStorageGet(storage: Storage | null | undefined, key: string): string | null {
  try {
    if (!storage) return null;
    return storage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * Safely sets a value in storage.
 * Silently fails if storage is unavailable or throws.
 */
export function safeStorageSet(storage: Storage | null | undefined, key: string, value: string): void {
  try {
    if (!storage) return;
    storage.setItem(key, value);
  } catch {
    // Silent failure — caller falls back to defaults
  }
}
